import { CalendarIcon, CameraOutlinedIcon, CheckmarkCircleIcon, PinOutlinedIcon } from '@village/icons';
import { Button, Text } from '@village/ui';
import { CenteredSpinner } from 'components/centered-spinner';
import { PrnBadge } from 'components/prn-badge';
import { useBooking, useIsMobile, useProviders, useIsSelectedAppointmentLoading } from 'hooks';
import { FC, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { formatDate } from 'utils/date';
import { getHyphenatedDepartmentAddress } from 'utils/department';
import { getAltText } from 'utils/image';

import * as Styled from './styles';

interface Props {
    mode?: 'CONFIRMATION';
}

const SelectedAppointmentDetailsCard: FC<Props> = ({ mode }) => {
    const { booking } = useBooking();
    const navigate = useNavigate();
    const { search } = useLocation();
    const isMobile = useIsMobile();
    const { data: providers } = useProviders(booking);
    const isAppointmentLoading = useIsSelectedAppointmentLoading(booking);

    const { reason, departmentAndRelatedDepartments, appointment } = booking;
    const provider = providers.find(({ providerid }) => providerid === appointment?.providerid);
    const department = departmentAndRelatedDepartments.find(({ departmentid }) => departmentid === appointment?.departmentid);
    const isConfirmationPageLayout = mode === 'CONFIRMATION';

    const sections = useMemo(
        () =>
            [
                {
                    title: 'Reason for Visit',
                    icon: <CheckmarkCircleIcon customSize={1.25} />,
                    value: {
                        testId: 'appointment-card-reason',
                        text: reason?.name,
                    },
                },
                {
                    title: 'Location',
                    icon: <PinOutlinedIcon customSize={1.25} />,
                    value: {
                        testId: 'appointment-card-department',
                        text: department && getHyphenatedDepartmentAddress(department),
                    },
                    disabled: !reason?.is_in_person,
                },
                {
                    title: 'Date & Time',
                    icon: <CalendarIcon customSize={1.25} />,
                    value: {
                        testId: 'appointment-card-time',
                        text: appointment && department ? formatDate(appointment.dateTimeParsed, 'appointmentDate') : null,
                    },
                    extraValue: {
                        testId: 'appointment-card-timezone',
                        text: `Timezone: ${department?.timezoneInfo.short} (${department?.timezoneInfo.full})`,
                    },
                },
                {
                    title: 'Visit Type',
                    icon: <CameraOutlinedIcon customSize={1.25} />,
                    value: {
                        testId: 'appointment-card-visit-type',
                        text: 'Video visit',
                    },
                    extraValue: {
                        text: 'Your provider will send you a link to access the appointment via email or text message shortly before the visit.',
                    },
                    disabled: reason?.is_in_person,
                },
            ].filter(({ disabled }) => !disabled),
        [reason, appointment, department]
    );

    const goToAppointmentsPage = useCallback(() => {
        navigate({ pathname: '/select-appointment', search }, { replace: true });
    }, [search, navigate]);

    if (isAppointmentLoading) {
        return (
            <Styled.Container>
                <Styled.Wrapper>
                    <CenteredSpinner />
                </Styled.Wrapper>
            </Styled.Container>
        );
    }

    if (!appointment) {
        return (
            <Styled.Container>
                <Styled.Wrapper>
                    <Styled.AppointmentNotAvailable data-testid="appointment-card-not-available">
                        <div>
                            <Text type="sub4">We’re sorry, the selected appointment is no longer available.</Text>
                            <Text type="body1">Please select a different appointment time.</Text>
                        </div>
                        <Button size="large" onClick={goToAppointmentsPage}>
                            Select new appointment
                        </Button>
                    </Styled.AppointmentNotAvailable>
                </Styled.Wrapper>
            </Styled.Container>
        );
    }

    return (
        <Styled.Container data-testid="selected-appointment-details-card">
            <Styled.Wrapper $isConfirmationPageLayout={isConfirmationPageLayout}>
                <Styled.ProviderSection $isConfirmationPageLayout={isConfirmationPageLayout}>
                    <Styled.ProviderImage
                        $isConfirmationPageLayout={isConfirmationPageLayout}
                        src={provider?.thumbnailUrl}
                        alt={getAltText(provider)}
                    />
                    <Styled.ProviderInfo $isConfirmationPageLayout={isConfirmationPageLayout}>
                        <Styled.ProviderName type="sub3" data-testid="appointment-card-provider">
                            {provider?.displayname}
                        </Styled.ProviderName>
                        {provider?.specialty ? (
                            <Styled.ProviderSpeciality
                                $isMobile={isMobile}
                                $isConfirmationPageLayout={isConfirmationPageLayout}
                                type={isMobile && !isConfirmationPageLayout ? 'body2' : 'caption1'}
                            >
                                {provider.specialty}
                            </Styled.ProviderSpeciality>
                        ) : null}
                        {provider?.isprn && isMobile ? (
                            <Styled.PrnSection data-testid="prn-notice">
                                <PrnBadge placement="bottom" />
                            </Styled.PrnSection>
                        ) : null}
                        {provider?.isprn && !isMobile ? (
                            <Styled.PrnSection data-testid="prn-notice">
                                <Styled.PrnIcon />
                                <div>
                                    <Text tag="span" type="h6">
                                        Note:{' '}
                                    </Text>
                                    This provider does not regularly practice at this location. Follow-up appointments might have
                                    to be booked with another provider.
                                </div>
                            </Styled.PrnSection>
                        ) : null}
                    </Styled.ProviderInfo>
                </Styled.ProviderSection>

                <Styled.Sections
                    $isConfirmationPageLayout={isConfirmationPageLayout}
                    $isPrn={!!provider?.isprn}
                    data-testid="extra-section"
                >
                    {sections.map((section) => (
                        <Styled.Section key={section.title} $isConfirmationPageLayout={isConfirmationPageLayout}>
                            {section.icon}
                            <Styled.InlineInfo>
                                <Styled.InfoTitle type="h6" $isConfirmationPageLayout={isConfirmationPageLayout}>
                                    {section.title}
                                </Styled.InfoTitle>
                                <Styled.InfoValue type="sub4" data-testid={section.value.testId}>
                                    {section.value.text}
                                </Styled.InfoValue>
                                {section.extraValue ? (
                                    <Styled.ExtraInfoValue type="body1" data-testid={section.extraValue.testId}>
                                        {section.extraValue.text}
                                    </Styled.ExtraInfoValue>
                                ) : null}
                            </Styled.InlineInfo>
                        </Styled.Section>
                    ))}
                </Styled.Sections>
            </Styled.Wrapper>
        </Styled.Container>
    );
};

export { SelectedAppointmentDetailsCard };
